import React, { useEffect, useState } from "react";
import developerApi from "../api/developerApi";

const pageStyle = {
    padding: "24px",
    display: "grid",
    gap: "20px",
    maxWidth: "1500px",
    margin: "0 auto",
    boxSizing: "border-box",
};

const cardStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #d9dee8",
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
};

const sectionTitleStyle = {
    margin: 0,
    fontSize: "20px",
    fontWeight: 700,
    color: "#111827",
};

const labelStyle = {
    display: "block",
    textAlign: "left",
    marginBottom: "6px",
    fontWeight: 600,
    fontSize: "14px",
    color: "#111827",
};

const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    fontSize: "14px",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
};

const primaryButtonStyle = {
    padding: "10px 16px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 700,
};

const secondaryButtonStyle = {
    padding: "10px 16px",
    backgroundColor: "#ffffff",
    color: "#111827",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 700,
};

const mutedTextStyle = {
    margin: 0,
    color: "#64748b",
    fontSize: "14px",
    lineHeight: 1.5,
};

const DEFAULT_SETTINGS = {
    developer_login: "",
    developer_password: "",
    message_orders: false,
};

const FIELD_LABELS = {
    developer_login: "Логин",
    developer_password: "Пароль",
    message_orders: "Уведомления об успешной выгрузке",
};

const SECTIONS = [
    {
        key: "auth",
        title: "Admin / Auth",
        collapsible: false,
        fields: ["developer_login", "developer_password"],
    },
    {
        key: "notifications",
        title: "Уведомления",
        collapsible: false,
        fields: ["message_orders"],
    },
];

const pickAdminFields = (settings) => ({
    developer_login: settings?.developer_login || "",
    developer_password: settings?.developer_password || "",
    message_orders: Boolean(settings?.message_orders),
});

const DeveloperPanel = ({ authUser }) => {
    const [currentSetting, setCurrentSetting] = useState(DEFAULT_SETTINGS);
    const [error, setError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSettings = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await developerApi.getSetting();
                setCurrentSetting(pickAdminFields(data));
            } catch (err) {
                console.error("Ошибка загрузки настроек:", err);
                setError("Не удалось загрузить настройки.");
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, [authUser.developer_login]);

    const handleFieldChange = (key, value) => {
        setCurrentSetting((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSave = async () => {
        setError(null);
        setSaveSuccess("");

        try {
            await developerApi.updateSettingFields(currentSetting);
            setSaveSuccess("Изменения сохранены.");
        } catch (err) {
            console.error("Ошибка при сохранении настроек:", err);
            setError("Не удалось сохранить настройки.");
        }
    };

    const handleReset = async () => {
        setError(null);
        setSaveSuccess("");
        setLoading(true);

        try {
            const data = await developerApi.getSetting();
            setCurrentSetting(pickAdminFields(data));
        } catch (err) {
            console.error("Ошибка при сбросе настроек:", err);
            setError("Не удалось перезагрузить настройки.");
        } finally {
            setLoading(false);
        }
    };

    const renderField = (fieldKey) => {
        const value = currentSetting[fieldKey];
        const isCheckbox = fieldKey === "message_orders";
        const isPassword = fieldKey === "developer_password";
        const isReadonly = fieldKey === "developer_login";

        return (
            <div
                key={fieldKey}
                style={{
                    display: "grid",
                    gap: "6px",
                    alignContent: "start",
                }}
            >
                <label style={labelStyle}>{FIELD_LABELS[fieldKey]}</label>

                {isCheckbox ? (
                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            minHeight: "42px",
                            padding: "6px 0",
                            color: "#111827",
                            fontWeight: 500,
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={Boolean(value)}
                            onChange={(e) => handleFieldChange(fieldKey, e.target.checked)}
                            style={{ width: "18px", height: "18px" }}
                        />
                        <span>{Boolean(value) ? "Включено" : "Выключено"}</span>
                    </label>
                ) : (
                    <input
                        type={isPassword ? "password" : "text"}
                        value={value || ""}
                        onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                        style={{
                            ...inputStyle,
                            backgroundColor: isReadonly ? "#f8fafc" : "#ffffff",
                        }}
                        disabled={isReadonly}
                    />
                )}
            </div>
        );
    };

    return (
        <div style={pageStyle}>
            <div style={{ ...cardStyle, padding: "20px 24px" }}>
                <h1 style={{ margin: 0, fontSize: "28px", color: "#111827" }}>Админ</h1>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr)",
                    gap: "20px",
                }}
            >
                <div style={{ ...cardStyle, padding: "18px 20px", display: "grid", gap: "16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            <button type="button" style={primaryButtonStyle} onClick={handleSave}>
                                Сохранить
                            </button>
                            <button type="button" style={secondaryButtonStyle} onClick={handleReset}>
                                Сбросить
                            </button>
                        </div>
                    </div>

                    {error ? <div style={{ color: "#b91c1c", fontWeight: 600 }}>{error}</div> : null}
                    {saveSuccess ? <div style={{ color: "#166534", fontWeight: 600 }}>{saveSuccess}</div> : null}
                    {loading ? <div style={mutedTextStyle}>Загрузка настроек…</div> : null}
                </div>

                {SECTIONS.map((section) => {
                    const isOpen = true;
                    return (
                        <div key={section.key} style={{ ...cardStyle, padding: "18px 20px", display: "grid", gap: "16px" }}>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    gap: "12px",
                                    flexWrap: "wrap",
                                }}
                            >
                                <div style={{ display: "grid", gap: "6px" }}>
                                    <h2 style={sectionTitleStyle}>{section.title}</h2>
                                </div>
                            </div>

                            {isOpen ? (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                                        gap: "16px 20px",
                                    }}
                                >
                                    {section.fields.map(renderField)}
                                </div>
                            ) : null}
                        </div>
                    );
                })}

            </div>
        </div>
    );
};

export default DeveloperPanel;
